/**
 * @engine/vfx — Baked gradient/curve sampler functions.
 *
 * Gradient and curve port data is instance data, not a runtime value — so the
 * compiler bakes each unique dataset into a module-level Slang function:
 *
 *   float4 grad_<hash>(float t) { ... }
 *   float  curve_<hash>(float t) { ... }
 *
 * Node bodies call the port by name (`gradient(t)`); the emitter binds that
 * call to the baked function. Identical data across instances/effects hashes
 * to the same function name and dedupes.
 */

import { formatFloat } from './slangTypes';
import { contentHash } from './scan';

export interface GradientStop {
  t: number;
  color: { r: number; g: number; b: number; a: number; intensity?: number };
}

export interface CurvePoint {
  t: number;
  value?: number;
  v?: number; // alternative property name used in serialized JSON
}

export interface BakedSampler {
  /** Slang function name (content-addressed). */
  readonly name: string;
  /** Full Slang function source. */
  readonly code: string;
}

/** Normalize the two serialized gradient shapes into a stop list. */
export function normalizeGradient(value: unknown): GradientStop[] {
  if (Array.isArray(value)) return value as GradientStop[];
  if (value && typeof value === 'object' && 'stops' in (value as object)) {
    const stops = (value as { stops?: GradientStop[] }).stops;
    return Array.isArray(stops) ? stops : [];
  }
  return [];
}

/** Normalize the two serialized curve shapes into a point list. */
export function normalizeCurve(value: unknown): CurvePoint[] {
  if (Array.isArray(value)) return value as CurvePoint[];
  if (value && typeof value === 'object' && 'points' in (value as object)) {
    const points = (value as { points?: CurvePoint[] }).points;
    return Array.isArray(points) ? points : [];
  }
  return [];
}

function colorToSlang(c: GradientStop['color']): string {
  const intensity = c.intensity ?? 1.0;
  return `float4(${formatFloat(c.r * intensity)}, ${formatFloat(c.g * intensity)}, ${formatFloat(c.b * intensity)}, ${formatFloat(c.a)})`;
}

/** Bake gradient stops into a `float4(float t)` sampler. */
export function bakeGradientSampler(stops: GradientStop[]): BakedSampler {
  const sorted = [...stops].sort((a, b) => a.t - b.t);
  const name = `grad_${contentHash(JSON.stringify(sorted))}`;

  let body: string;
  if (sorted.length === 0) {
    body = '    return float4(1.0, 1.0, 1.0, 1.0);';
  } else if (sorted.length === 1) {
    body = `    return ${colorToSlang(sorted[0].color)};`;
  } else {
    const lines: string[] = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      const s0 = sorted[i];
      const s1 = sorted[i + 1];
      const tRange = s1.t - s0.t;
      const tNorm = tRange > 0.0001
        ? `(t - ${formatFloat(s0.t)}) / ${formatFloat(tRange)}`
        : '0.0';
      const blend = `lerp(${colorToSlang(s0.color)}, ${colorToSlang(s1.color)}, clamp(${tNorm}, 0.0, 1.0))`;
      if (i < sorted.length - 2) {
        lines.push(`    if (t < ${formatFloat(s1.t)}) { return ${blend}; }`);
      } else {
        lines.push(`    return ${blend};`);
      }
    }
    body = lines.join('\n');
  }

  return {
    name,
    code: `float4 ${name}(float t)\n{\n${body}\n}`,
  };
}

/** Bake curve points into a `float(float t)` sampler. */
export function bakeCurveSampler(points: CurvePoint[]): BakedSampler {
  const getValue = (p: CurvePoint): number => p.value ?? p.v ?? 0;
  const sorted = [...points].sort((a, b) => a.t - b.t);
  const name = `curve_${contentHash(JSON.stringify(sorted.map(p => [p.t, getValue(p)])))}`;

  let body: string;
  if (sorted.length === 0) {
    body = '    return 1.0;';
  } else if (sorted.length === 1) {
    body = `    return ${formatFloat(getValue(sorted[0]))};`;
  } else {
    const lines: string[] = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      const p0 = sorted[i];
      const p1 = sorted[i + 1];
      const tRange = p1.t - p0.t;
      const tNorm = tRange > 0.0001
        ? `(t - ${formatFloat(p0.t)}) / ${formatFloat(tRange)}`
        : '0.0';
      const blend = `lerp(${formatFloat(getValue(p0))}, ${formatFloat(getValue(p1))}, clamp(${tNorm}, 0.0, 1.0))`;
      if (i < sorted.length - 2) {
        lines.push(`    if (t < ${formatFloat(p1.t)}) { return ${blend}; }`);
      } else {
        lines.push(`    return ${blend};`);
      }
    }
    body = lines.join('\n');
  }

  return {
    name,
    code: `float ${name}(float t)\n{\n${body}\n}`,
  };
}
